<script setup lang="ts">
import AddressInput from '@/components/session/kyc/address/AddressInput.vue';
import { Address } from '@/models/Address';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import countries from 'i18n-iso-countries';
import { computed, ref, type Ref } from 'vue';
import { type AddressInputStatus } from '@/components/session/kyc/address/address.types';
import { watchDebounced } from '@vueuse/core';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const addressInputStatus: Ref<AddressInputStatus> = ref('Input');

const model = defineModel({
  type: Address,
  required: true,
});

const props = defineProps({
  regionOverride: {
    type: String,
    required: true,
  },
  countryOverride: {
    type: String,
    required: true,
  },
  switchMode: {
    type: Function,
    required: true,
  },
  saveAddress: {
    type: Function,
    required: true,
  },
  isManualModeOnly: {
    type: Boolean,
    required: true,
  },
});

const address = ref<Address>(Address.create({ ...model.value, country: props.countryOverride }));

const buttonText = computed(() =>
  addressInputStatus.value === 'Checking'
    ? 'Checking'
    : addressInputStatus.value === 'Saving'
      ? 'Saving'
      : 'Continue'
);

const isNextEnabled = ref(false);
const isValidationVisible = ref(false);
const isInitialCheck = ref(true);

function getAddressWithoutCountry(newAddress: Address) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { country, countryCode, fullAddress, ...addressPartsWithoutCountry } = newAddress;
  return addressPartsWithoutCountry;
}

function hasRequiredFields(newAddress: Address) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addressPartsWithoutCountry = getAddressWithoutCountry(newAddress);
  const isValidCountry = !!countries.getAlpha3Code(newAddress.country || '', 'en');
  const isPopulated = Object.values(addressPartsWithoutCountry).some((field) => field);

  return isValidCountry && isPopulated;
}

function isEmptyAddress(newAddress: Address) {
  const addressPartsWithoutCountry = getAddressWithoutCountry(newAddress);
  return Address.isEmpty(Address.create(addressPartsWithoutCountry));
}

async function checkData(updatedAddress: Address): Promise<void> {
  isNextEnabled.value = false;
  isValidationVisible.value = false;

  const newAddress = Address.create({
    ...updatedAddress,
    // we don't know what the full address is anymore. So we clear it and let the model handle it
    fullAddress: undefined,
  });

  addressInputStatus.value = 'Checking';

  if (hasRequiredFields(newAddress)) {
    isNextEnabled.value = true;
    isValidationVisible.value = false;

    model.value = newAddress;

    addressInputStatus.value = 'Input';
    return;
  }

  isNextEnabled.value = false;
  if (!isInitialCheck.value) {
    isValidationVisible.value = isEmptyAddress(newAddress);
  }

  isInitialCheck.value = false;

  addressInputStatus.value = 'Input';
}

watchDebounced(
  () => address.value,
  async (updatedAddress: Address) => {
    await checkData(updatedAddress);
  },
  {
    debounce: 500,
    deep: true,
  }
);

async function onSaveAddress() {
  addressInputStatus.value = 'Checking';

  await props.saveAddress();
  Mixpanel.instance.trackManualAddressSubmitted();
  return true;
}

async function init() {
  Mixpanel.instance.trackManualAddressFormOpened();
  await checkData(address.value);
}

let tracked = false;
function onBlur() {
  if (
    !tracked &&
    model.value.fullAddress &&
    address.value.fullAddress !== model.value.fullAddress
  ) {
    Mixpanel.instance.trackManualAddressEntered();
    // We want to capture the fact of user interaction with form
    // One event after form opened is enough
    tracked = true;
  }
}

init();
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col">
      <h1 class="text-3xl">What is your current residential address?</h1>
      <div class="mt-4 mb-4">Your address cannot be a PO box</div>

      <div
        class="flex flex-col overflow-x-hidden overflow-y-auto"
        data-test="screen-address-manual"
      >
        <div class="flex flex-col justify-between sm:flex-row">
          <AddressInput
            name="unitNumber"
            label="Unit number"
            placeholder="Unit number"
            widthClass="sm:w-[45%] w-[60%]"
            v-model="address.unitNumber"
            @blur="onBlur"
          />
          <AddressInput
            name="streetNumber"
            label="Street number"
            placeholder="Street number"
            widthClass="sm:w-[52%] w-[60%]"
            v-model="address.streetNumber"
            @blur="onBlur"
          />
        </div>
        <div class="flex flex-col justify-between sm:flex-row">
          <AddressInput
            name="streetName"
            label="Street name"
            placeholder="Enter street name"
            widthClass="sm:w-[52%] w-full"
            v-model="address.streetName"
            @blur="onBlur"
          />
          <AddressInput
            name="suburb"
            label="Suburb"
            placeholder="Enter suburb"
            widthClass="sm:w-[45%] w-full"
            v-model="address.suburb"
            @blur="onBlur"
          />
        </div>
        <div class="flex flex-col justify-between sm:flex-row">
          <AddressInput
            name="town"
            label="Town/City"
            placeholder="Enter town/city"
            widthClass="sm:w-[45%] w-full"
            v-model="address.town"
            @blur="onBlur"
          />
          <AddressInput
            name="state"
            label="Region/State"
            placeholder="Enter region/state"
            widthClass="sm:w-[52%] w-full"
            v-model="address.state"
            @blur="onBlur"
          />
        </div>
        <div class="flex flex-col justify-between sm:flex-row">
          <AddressInput
            name="country"
            label="Country"
            placeholder="Enter country"
            :disabled="true"
            widthClass="sm:w-[67%] w-full"
            v-model="address.country"
            @blur="onBlur"
          />
          <AddressInput
            name="postalCode"
            label="Post code"
            placeholder="Post code"
            widthClass="sm:w-[30%] w-[60%]"
            v-model="address.postalCode"
            @blur="onBlur"
          />
        </div>
      </div>

      <p
        class="text-light-utility-red dark:text-dark-utility-red mt-1 py-0"
        v-if="isValidationVisible"
        data-test="address-validation-error"
      >
        <span
          >We could not validate this address. Please review the country name and ensure all other
          fields are correctly filled in.</span
        >
      </p>
      <a
        v-if="!isManualModeOnly"
        href="#"
        class="text-light-secondary dark:text-dark-border mt-2 mb-16 text-sm font-semibold underline"
        @click.prevent="props.switchMode()"
        data-test="change-mode"
      >
        <span>Address autocomplete mode</span>
      </a>
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
      <FlowNavigationButton
        :preNavigationAction="onSaveAddress"
        :text="buttonText"
        :disabled="!isNextEnabled"
        direction="forward"
      />
    </template>
  </KycSessionLayout>
</template>
